import React, { useState, useMemo } from 'react';
import { Input, Spinner } from '../../components/common';
import { Search } from 'iconoir-react';
import { NetworkMatches, AiMatchAnalyticsCard } from '../../components/dashboard';
import { useDashboardAiMatchAnalytics } from '../../hooks/useDashboard';
import { Tour } from '../../components/common/Tour';
import { aiMatchAnalytics as dashboardAiMatchAnalytics } from '../../lib/constants';
import { getAiMatchAnalyticsColors } from '../../utils';
import type { AiMatchAnalyticsItem } from '../../types/dashboard';

const MatchesPage = () => {
  const [search, setSearch] = useState('');
  // Multi-event: the event filter lives here (rendered by NetworkMatches) so
  // the stats header rescopes together with the tabs and lists.
  const [eventId, setEventId] = useState<string | null>(null);
  const { data: analytics, isLoading } = useDashboardAiMatchAnalytics(eventId);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  // Apr-25: was 3 hardcoded stat cards inline (no info tooltips
  // inconsistent text colors). Now reuses AiMatchAnalyticsCard with the
  // shared `dashboardAiMatchAnalytics` constants so the dashboard tooltip
  // copy ("Average match score", "Response rate", "Connection rate") and
  // the rotating color palette are applied consistently across both
  // surfaces. Same metrics — pulled from useDashboardAiMatchAnalytics —
  // just rendered through the shared component.
  const analyticsItems: AiMatchAnalyticsItem[] = useMemo(() => {
    if (!analytics?.result) return [];
    const r = analytics.result;
    // Jun-19: avg_match_score can be null (no AI-scored matches yet) -> pass it
    // through so the card shows N/A. Rates are real numbers, default 0.
    const values = [r.avg_match_score ?? null, r.response_rate ?? 0, r.connection_rate ?? 0];
    return dashboardAiMatchAnalytics.map((card, index) => ({
      ...card,
      percentage: values[index],
    }));
  }, [analytics]);

  return (
    <div className='px-4 py-6'>
      <Tour name='matches' />
      <div className='flex items-center justify-between mb-6'>
        <div>
          <h3 className='!font-medium text-heading-500 mb-1'>
            Matches
          </h3>
          <p className='text-sm text-body-400 font-medium mb-0'>
            Please review the most recent match results we have located for you.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className='flex items-center justify-center my-13'>
          <Spinner size='md' color='primary' />
        </div>
      ) : (
        <div className='grid grid-cols-3 sm:gap-6 gap-3 mb-6 sm:mb-12'>
          {analyticsItems.map((item, index) => {
            const { bg = '', text = '' } = getAiMatchAnalyticsColors(index) || {};
            return (
              <AiMatchAnalyticsCard
                key={item.title}
                item={item}
                bg={bg}
                text={text}
                isLast={false}
              />
            );
          })}
        </div>
      )}

      <div className='border-2 border-purple-25 bg-gray-light-50 rounded-xl sm:p-6 p-4'>
        <div className='grid md:grid-cols-2 gap-6 mb-6'>
          <div>
            <h5 className='!font-medium text-heading-500 mb-1'>
              Your matches
            </h5>
            <p className='text-sm text-body-400 font-medium mb-0'>
              Recent matches found by your AI agent
            </p>
          </div>
          <Input
            type='text'
            placeholder='Please enter the name or core objective to search'
            prefix={<Search className='text-primary-500 w-5 h-5' />}
            value={search}
            onChange={handleSearch}
          />
        </div>

        <NetworkMatches
          search={search}
          setSearch={setSearch}
          eventId={eventId}
          onEventIdChange={setEventId}
        />
      </div>
    </div>
  );
};

export default MatchesPage;
